import { FunctionComponent, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AuthProps } from "../../api/auth";
import AuthCode from "../../types/auth-code";
import { ActiveTab } from "./active-tab";
import Alert from "./alert";
import ForgotTab from "./forgot-tab";

interface ILoginTabProps {
  customerType: string;
  username: string;
  password: string;
  handleLogin: (e: AuthProps) => void;
  handleContinue: (e: AuthCode) => void;
  alert: boolean;
  forgotAlert: boolean;

  setUsername: (username: string) => void;
  setPassword: (password: string) => void;
  setActiveTab: (activeTab: ActiveTab) => void;
}

const LoginTab: FunctionComponent<ILoginTabProps> = ({
  customerType,
  forgotAlert,
  username,
  password,
  alert,
  handleLogin,
  handleContinue,
  setUsername,
  setPassword,
  setActiveTab,
}) => {
  const [toggle, setToggle] = useState(true);
  const handleToggle = () => setToggle((toggle) => !toggle);

  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AuthProps>();

  useEffect(() => {
    reset({
      username: username,
      password: password,
    });
  }, [username, password, reset]);

  return (
    <>
      <div className="signup-form-head-section mb-10 flex-col-reverse flex">
        {toggle && (
          <>
            <h3 className="head-title pt-4">
              {t(`Login.Welcome.${customerType}`)}
            </h3>
            <span className={`sepanta-icon-logout icon-box`}></span>
          </>
        )}
      </div>

      {toggle ? (
        <form onSubmit={handleSubmit(handleLogin)}>
          {alert ? <Alert color="red" message={t("Login.Error")} /> : null}
          <div className="form-section rtl flex flex-grow flex-col gap-4 mb-0">
            <div className="form-group flex flex-col w-full rtl text-right mt-6 relative">
              <label htmlFor="username" className="pr-4">
                {t(`Login.Username.${customerType}.Label`)} *
              </label>
              <input
                maxLength={12}
                {...register("username")}
                type="hidden"
                value={username}
                className="form-control form-control-lg pl-4 shadow-none"
              />
              <input
                id="username"
                maxLength={12}
                {...register("username")}
                onChange={(e) => {
                  register("username").onChange(e);
                  setUsername(e.target.value);
                }}
                type="phone"
                className="border-2 p-4 rounded-lg mt-2 text-left tracking-wider"
                placeholder={t(`Login.Username.${customerType}.PlaceHolder`)}
              />
              <p className="absolute left-0 text-red-500">
                {errors.username?.message}
              </p>
              <span className="sepanta-icon-mobile absolute top-12 left-2 icon-orange"></span>
            </div>

            <div className="w-full form-group flex flex-col rtl text-right mt-0 relative">
              <label htmlFor="password" className="pr-4">
                {t("Login.Password.Label")} *
              </label>
              <input
                {...register("password")}
                onChange={(e) => {
                  register("password").onChange(e);
                  setPassword(e.target.value);
                }}
                type="password"
                className="border-2 p-4 rounded-lg mt-2 text-left tracking-wider"
                placeholder={t("Login.Password.PlaceHolder")}
              />
              <p className="absolute left-0 text-red-500">
                {errors.password?.message}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-start flex-row-reverse">
            <input
              className="mr-5"
              id="lbp_checkbox"
              type="checkbox"
              checked={toggle}
              onChange={handleToggle}
            />
            <label
              htmlFor="lbp_checkbox"
              className="ml-2 text-sm font-normal text-gray-900 pr-2"
            >
              {t("Login.WithPass")}
            </label>
          </div>

          <div className="form-section rtl flex flex-col gap-4 justify-between pb-10">
            <button
              type="submit"
              className="signup-btn p-5 flex text-center justify-center rounded-xl"
            >
              {t("Login.Submit")}
              <span className="sepanta-icon-left-long-arrow"></span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("forgot")}
              className="flex items-center justify-center navigate-link-bd"
            >
              {t("Login.ForgotPassword")}
            </button>

            <a
              href="https://my.sepanta.com/signup"
              className="flex items-center justify-center navigate-link-bd"
            >
              {t("Login.Signup.Link")}
            </a>
          </div>
        </form>
      ) : (
        <ForgotTab
          alert={forgotAlert}
          customerType={customerType}
          username={username}
          setUsername={setUsername}
          setActiveTab={setActiveTab}
          handleContinue={handleContinue}
          toggle={true}
          handleToggle={handleToggle}
        />
      )}
    </>
  );
};

export default LoginTab;
